import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import { Container, FilterSelect, HeaderContainer, Title } from "../styles";
import Table from "../../../components/Table";
import { notification, Select, Tooltip } from "antd";
import { ContentBordered, FilterContainer } from "./styles";
import { useSelector } from "react-redux";
import {
  getInsuranceAnalyticsService,
  getInsuranceDashboardFiltersService,
} from "../../../service/insurance-analytics.service";
import {
  getReportFilterEventsByYearService,
  getReportFilterYearsService,
} from "../../../service/reportFilter.service";

const AnalyticsInsurance = () => {
  const intl = useIntl();
  const { Option } = Select;
  const { token } = useSelector((state) => state.auth);

  // Table state
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("requiredInsurances");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loadingTable, setLoadingTable] = useState(false);

  // Filter options state
  const [yearsOptions, setYearsOptions] = useState([]);
  const [eventsOptions, setEventsOptions] = useState([]);
  const [flowsOptions, setFlowsOptions] = useState([]);
  const [documentsOptions, setDocumentsOptions] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Selected filters state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const canQuery = Boolean(selectedYear) && Boolean(selectedEventId);

  useEffect(() => {
    fetchFilters();
    fetchYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch filters with cross-filtering support
  const fetchFilters = useCallback(
    async (eventId = null, flowId = null, documentId = null) => {
      setLoadingFilters(true);
      try {
        const response = await getInsuranceDashboardFiltersService({
          token,
          eventId,
          uploadFlowId: flowId,
          documentConfigId: documentId,
        });

        const { uploadFlows = [], documentConfigs = [] } = response.data;

        setFlowsOptions(uploadFlows);
        setDocumentsOptions(documentConfigs);
      } catch (error) {
        console.error("Error fetching filters:", error);
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "api.error." + (error.response?.data?.code || "default"),
          }),
        });
      } finally {
        setLoadingFilters(false);
      }
    },
    [token, intl]
  );

  const fetchYears = useCallback(async () => {
    try {
      const response = await getReportFilterYearsService({ token });

      setYearsOptions(
        (response.data?.output ?? []).map((year) => ({
          label: String(year),
          value: year,
        }))
      );
    } catch (error) {
      setYearsOptions([]);
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "api.error." + (error.response?.data?.code || "default"),
        }),
      });
    }
  }, [token, intl]);

  // Fetch analytics data
  const fetchAnalyticsData = useCallback(
    async (
      page = currentPage,
      size = rowsPerPage,
      sortField = sortBy,
      sortDir = sortDirection,
      eventId = selectedEventId,
      flowId = selectedFlowId,
      documentId = selectedDocumentId
    ) => {
      setLoadingTable(true);
      try {
        const response = await getInsuranceAnalyticsService({
          token,
          eventId: eventId,
          uploadFlowId: flowId,
          documentConfigId: documentId,
          year: selectedYear,
          page: page - 1, // API uses 0-indexed pages
          size,
          sortBy: sortField,
          sortDirection: sortDir.toUpperCase(),
        });

        const { items = [], paginationInfo } = response.data;

        setTableData(items);
        setTotalRows(paginationInfo.totalElements || 0);
        setCurrentPage(page);
        setRowsPerPage(paginationInfo.maxSize || size);
      } catch (error) {
        console.error("Error fetching analytics data:", error);

        const code = error.response?.data?.code;
        if (code === -48013) {
          // No data found - empty state
          setTableData([]);
          setTotalRows(0);
          return;
        }

        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "api.error." + (error.response?.data?.code || "default"),
          }),
        });

        setTableData([]);
        setTotalRows(0);
      } finally {
        setLoadingTable(false);
      }
    },
    [
      token,
      currentPage,
      rowsPerPage,
      sortBy,
      sortDirection,
      selectedEventId,
      selectedFlowId,
      selectedDocumentId,
      selectedYear,
      intl,
    ]
  );

  const handleYearChange = useCallback(
    async (value) => {
      const year = value || null;

      setSelectedYear(year);
      setSelectedEventId(null);
      setSelectedFlowId(null);
      setSelectedDocumentId(null);
      setEventsOptions([]);
      setTableData([]);
      setTotalRows(0);
      setCurrentPage(1);

      if (!year) {
        fetchFilters();
        return;
      }

      setLoadingFilters(true);
      try {
        const response = await getReportFilterEventsByYearService({
          token,
          year,
        });

        setEventsOptions(response.data?.output ?? []);

        fetchFilters();
      } catch (error) {
        setEventsOptions([]);
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "api.error." + (error.response?.data?.code || "default"),
          }),
        });
      } finally {
        setLoadingFilters(false);
      }
    },
    [token, intl, fetchFilters]
  );

  // Handle event filter change
  const handleEventChange = useCallback(
    (value) => {
      const eventId = value || null;
      setSelectedEventId(eventId);
      setSelectedFlowId(null);
      setSelectedDocumentId(null);
      setCurrentPage(1);

      // Fetch filters with new selection
      fetchFilters(eventId, null, null);

      if (!eventId) {
        setTableData([]);
        setTotalRows(0);
        return;
      }

      // Fetch data immediately with the new eventId
      fetchAnalyticsData(1, rowsPerPage, sortBy, sortDirection, eventId, null, null);
    },
    [rowsPerPage, sortBy, sortDirection, fetchFilters, fetchAnalyticsData]
  );

  // Handle flow filter change
  const handleFlowChange = useCallback(
    (value) => {
      const flowId = value || null;
      setSelectedFlowId(flowId);
      setCurrentPage(1);

      // Fetch filters with new selection
      fetchFilters(selectedEventId, flowId, selectedDocumentId);

      if (!canQuery) {
        return;
      }

      // Fetch data immediately with the new flowId
      fetchAnalyticsData(
        1,
        rowsPerPage,
        sortBy,
        sortDirection,
        selectedEventId,
        flowId,
        selectedDocumentId
      );
    },
    [
      selectedEventId,
      selectedDocumentId,
      rowsPerPage,
      sortBy,
      sortDirection,
      canQuery,
      fetchFilters,
      fetchAnalyticsData,
    ]
  );

  // Handle document filter change
  const handleDocumentChange = useCallback(
    (value) => {
      const documentId = value || null;
      setSelectedDocumentId(documentId);
      setCurrentPage(1);

      // Fetch filters with new selection
      fetchFilters(selectedEventId, selectedFlowId, documentId);

      if (!canQuery) {
        return;
      }

      // Fetch data immediately with the new documentId
      fetchAnalyticsData(
        1,
        rowsPerPage,
        sortBy,
        sortDirection,
        selectedEventId,
        selectedFlowId,
        documentId
      );
    },
    [
      selectedEventId,
      selectedFlowId,
      rowsPerPage,
      sortBy,
      sortDirection,
      canQuery,
      fetchFilters,
      fetchAnalyticsData,
    ]
  );

  // Handle pagination change
  const handlePageChange = useCallback(
    (page) => {
      if (!canQuery) return;

      setCurrentPage(page);
      fetchAnalyticsData(page, rowsPerPage, sortBy, sortDirection);
    },
    [canQuery, rowsPerPage, sortBy, sortDirection, fetchAnalyticsData]
  );

  // Handle rows per page change
  const handlePerRowsChange = useCallback(
    (newPerPage, page) => {
      if (!canQuery) return;

      setRowsPerPage(newPerPage);
      setCurrentPage(page);
      fetchAnalyticsData(page, newPerPage, sortBy, sortDirection);
    },
    [canQuery, sortBy, sortDirection, fetchAnalyticsData]
  );

  // Handle sort change
  const handleSort = useCallback(
    (column, direction) => {
      if (!canQuery) return;

      setSortBy(column.sortField);
      setSortDirection(direction);
      fetchAnalyticsData(currentPage, rowsPerPage, column.sortField, direction);
    },
    [canQuery, currentPage, rowsPerPage, fetchAnalyticsData]
  );

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        id: "eventEditionName",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "events" })}
          </div>
        ),
        selector: (row) => row.eventEditionName,
        sortField: "eventEditionName",
        sortable: true,
        width: "15%",
        cell: (row) => {
          const text = row.eventEditionName || "";
          return (
            <Tooltip title={text} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: "flowName",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "flow" })}
          </div>
        ),
        selector: (row) => row.flowName,
        sortField: "flowName",
        sortable: true,
        width: "15%",
        cell: (row) => {
          const text = row.flowName || "";
          return (
            <Tooltip title={text} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: "documentName",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "document" })}
          </div>
        ),
        selector: (row) => row.documentName,
        sortField: "documentName",
        sortable: true,
        width: "12%",
        cell: (row) => {
          const text = row.documentName || "";
          return (
            <Tooltip title={text} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: "requiredInsurances",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "insurance_required" })}
          </div>
        ),
        selector: (row) => row.requiredInsurances,
        sortField: "requiredInsurances",
        sortable: true,
        width: "8%",
        center: true,
      },
      {
        id: "partnerInsurances",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "insurance_partner" })}
          </div>
        ),
        selector: (row) => row.partnerInsurances,
        sortField: "partnerInsurances",
        sortable: true,
        width: "8%",
        center: true,
      },
      {
        id: "contractInsurances",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "insurance_contract" })}
          </div>
        ),
        selector: (row) => row.contractInsurances,
        sortField: "contractInsurances",
        sortable: true,
        width: "8%",
        center: true,
      },
      {
        id: "clientInsurances",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "insurance_client" })}
          </div>
        ),
        selector: (row) => row.clientInsurances,
        sortField: "clientInsurances",
        sortable: true,
        width: "8%",
        center: true,
      },
      {
        id: "ecommerceInsurances",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "insurance_ecommerce" })}
          </div>
        ),
        selector: (row) => row.ecommerceInsurances,
        sortField: "ecommerceInsurances",
        sortable: true,
        width: "8%",
        center: true,
      },
      {
        id: "pendingInsurances",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "insurance_pending" })}
          </div>
        ),
        selector: (row) => row.pendingInsurances,
        sortField: "pendingInsurances",
        sortable: true,
        width: "8%",
        center: true,
      },
      {
        id: "dispensedInsurances",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "insurance_dispensed" })}
          </div>
        ),
        selector: (row) => row.dispensedInsurances,
        sortField: "dispensedInsurances",
        sortable: true,
        width: "8%",
        center: true,
      },
    ],
    [intl]
  );

  return (
    <AnimatedMainContainer
      breadcrumb={`
        ${intl.formatMessage({ id: "sidebar.upload_documents" })} |
        ${intl.formatMessage({ id: "insurance_analytics" })}
      `}
    >
      <Container>
        <ContentBordered>
          <HeaderContainer>
            <Title>
              <FormattedMessage id="insurance_analytics" />
            </Title>
          </HeaderContainer>

          <FilterContainer>
            <FilterSelect
              showSearch
              optionFilterProp="children"
              placeholder={intl.formatMessage({ id: "select_year" })}
              value={selectedYear}
              onChange={handleYearChange}
              allowClear
            >
              {yearsOptions.map((year) => (
                <Option key={year.value} value={year.value}>
                  {year.label}
                </Option>
              ))}
            </FilterSelect>

            <FilterSelect
              showSearch
              optionFilterProp="children"
              placeholder={intl.formatMessage({ id: "select_event" })}
              value={selectedEventId}
              onChange={handleEventChange}
              loading={loadingFilters}
              disabled={!selectedYear}
              allowClear
            >
              {eventsOptions.map((event) => (
                <Option key={event.idNumber} value={event.idNumber}>
                  {event.eventEditionName}
                </Option>
              ))}
            </FilterSelect>

            <FilterSelect
              showSearch
              optionFilterProp="children"
              placeholder={intl.formatMessage({ id: "select_flow" })}
              value={selectedFlowId}
              onChange={handleFlowChange}
              loading={loadingFilters}
              disabled={!selectedEventId}
              allowClear
            >
              {flowsOptions.map((flow) => (
                <Option key={flow.id} value={flow.id}>
                  {flow.name}
                </Option>
              ))}
            </FilterSelect>

            <FilterSelect
              showSearch
              optionFilterProp="children"
              placeholder={intl.formatMessage({ id: "select_document" })}
              value={selectedDocumentId}
              onChange={handleDocumentChange}
              loading={loadingFilters}
              disabled={!selectedEventId}
              allowClear
            >
              {documentsOptions.map((doc) => (
                <Option key={doc.id} value={doc.id}>
                  {/* Show "FileName (FlowName)" to differentiate documents with same name */}
                  {doc.uploadFlowName ? `${doc.fileName} (${doc.uploadFlowName})` : doc.fileName}
                </Option>
              ))}
            </FilterSelect>
          </FilterContainer>

          <Table
            sortServer={true}
            data={tableData}
            columns={columns}
            progressPending={loadingTable}
            paginationServer={true}
            paginationTotalRows={totalRows}
            paginationCurrentPage={currentPage}
            paginationPerPage={rowsPerPage}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            onSort={handleSort}
            noDataComponent={
              <div style={{ padding: "24px", textAlign: "center" }}>
                {!selectedYear
                  ? intl.formatMessage({ id: "report_filter_year_required" })
                  : !selectedEventId
                  ? intl.formatMessage({ id: "report_filter_event_required" })
                  : intl.formatMessage({ id: "no_records_to_display" })}
              </div>
            }
          />
        </ContentBordered>
      </Container>
    </AnimatedMainContainer>
  );
};

export default AnalyticsInsurance;
